import React, { useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import confetti from 'canvas-confetti';
import './LuckyWheel.css';

const socket = io();
// Added empty string at the end for "Blank" mapping
const CHAR_MAP = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ ".split(''); 
const STRIP_HEIGHT_SINGLE = 140; 

// --- BATCH CARD COMPONENT ---
const SpinCard = ({ status, result }) => {
    // status: 'waiting' | 'spinning' | 'revealed'
    const isSpinning = status === 'spinning';
    const isRevealed = status === 'revealed';
    
    // Static strip for visual noise
    const renderStrip = () => {
        const items = [];
        for(let i=0; i<40; i++) items.push(<div key={i} className="batch-number-item">{Math.floor(Math.random()*10)}</div>);
        return items;
    };

    return (
        <div className={`spin-card ${isRevealed ? 'active' : 'is-waiting'}`}>
            <div className="card-avatar">
                {isRevealed && result && result.avatar_url ? (
                    <img src={'/uploads/' + result.avatar_url} alt="" />
                ) : (
                    <span className="icon-user" style={{fontSize:30, color:'#ccc'}}>?</span>
                )}
            </div>
            
            <div className="card-name">
                {isRevealed && result ? (result.full_name || result.employee_code) : '...'}
            </div>

            <div className="card-window" style={{borderColor: isRevealed ? '#2ecc71' : '#555'}}>
                {isSpinning ? ( 
                    <div className="batch-card-strip is-spinning">
                        {renderStrip()}
                    </div> 
                ) : (
                    <div className="card-number">
                        {isRevealed && result ? result.employee_code : ''}
                    </div>
                )}
            </div>
        </div>
    );
};

const LuckyBoard = () => {
    // Data
    const [stats, setStats] = useState(0);
    const [winners, setWinners] = useState([]);
    const [session, setSession] = useState(null); 
    
    // Inputs
    const [inputName, setInputName] = useState('Giải Khuyến Khích');
    const [inputQty, setInputQty] = useState(1);
    const [inputBatchSize, setInputBatchSize] = useState(1);

    // Animation State
    const [isSpinning, setIsSpinning] = useState(false);
    const [batchRevealedCount, setBatchRevealedCount] = useState(0); // For batch sequencing
    const [currentBatchWinners, setCurrentBatchWinners] = useState([]); 
    
    // Single Slot State
    const [slotOffsets, setSlotOffsets] = useState(new Array(9).fill(0));
    const staticStrip = useRef([]);
    
    // Popup
    const [popupWinners, setPopupWinners] = useState([]); 

    // Settings
    const [showSettings, setShowSettings] = useState(false);
    const [bgImage, setBgImage] = useState('https://img.freepik.com/free-vector/gradient-chinese-new-year-background_23-2151034598.jpg');
    const [overlayOpacity, setOverlayOpacity] = useState(0.5);
    const [spinStep, setSpinStep] = useState(0.5); // Default 0.5s delay
    const [uploadingBg, setUploadingBg] = useState(false);

    useEffect(() => {
        // Init Static Strip (Single Mode)
        let arr = [];
        for (let i = 0; i < 40; i++) arr = [...arr, ...CHAR_MAP];
        staticStrip.current = arr;

        fetchStats(); fetchWinners(); fetchSettings();
        
        socket.on('stats_update', (data) => { if (data.reset) fetchStats(); else fetchStats(); });
        socket.on('spin_result', (data) => startAnimation(data));
        socket.on('settings_update', (newSettings) => {
            if (newSettings.bgImage) setBgImage(newSettings.bgImage);
            if (newSettings.overlayOpacity !== undefined) setOverlayOpacity(newSettings.overlayOpacity);
            if (newSettings.spinStep !== undefined) setSpinStep(newSettings.spinStep);
        });
        socket.on('reset_data', () => { setWinners([]); setSession(null); fetchStats(); });
        
        return () => socket.off();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStats = async () => { try { const res = await axios.get('/api/stats'); setStats(res.data.total); } catch (e) { } };
    const fetchWinners = async () => { try { const res = await axios.get('/api/winners'); setWinners(res.data); } catch (e) { } };
    const fetchSettings = async () => { try { const res = await axios.get('/api/settings'); if(res.data.bgImage) setBgImage(res.data.bgImage); if(res.data.spinStep) setSpinStep(res.data.spinStep); } catch(e){} };

    const startSession = () => {
        if (!inputName || inputQty <= 0) return alert("Nhập tên giải & số lượng!");
        let batch = 1;
        if (parseInt(inputQty) > 1) {
            batch = parseInt(inputBatchSize);
            if (batch < 1) batch = 1; if (batch > parseInt(inputQty)) batch = parseInt(inputQty);
        }
        setSession({ name: inputName, total: parseInt(inputQty), remaining: parseInt(inputQty), batchSize: batch });
        setCurrentBatchWinners([]); setBatchRevealedCount(0);
    };
    
    const cancelSession = () => { if(window.confirm("Hủy giải này?")) { setSession(null); setCurrentBatchWinners([]); } };

    const handleMainButton = () => {
        if (session && session.remaining === 0) { setSession(null); setCurrentBatchWinners([]); return; }
        handleSpinClick();
    };

    const handleSpinClick = async () => {
        if (isSpinning || !session || session.remaining <= 0) return;
        
        // Reset Visuals
        setCurrentBatchWinners([]); 
        setBatchRevealedCount(0);
        
        let safeBatch = session.batchSize;
        if (safeBatch > session.remaining) safeBatch = session.remaining;
        
        try {
            const res = await axios.post('/api/spin', { prize_name: session.name, batch_size: safeBatch });
            if (!res.data.success) { alert(res.data.message); return; }
            setSession(prev => ({ ...prev, remaining: prev.remaining - Array.from(res.data.data).length }));
        } catch (e) { alert('Lỗi kết nối'); }
    };

    const startAnimation = (winnersArr) => {
        setIsSpinning(true);
        setCurrentBatchWinners(winnersArr);
        setPopupWinners([]);

        const isBatchMode = session && session.batchSize > 1;

        if (isBatchMode || winnersArr.length > 1) {
            runBatchAnimation(winnersArr);
        } else {
            runSlotAnimation(winnersArr);
        }
    };

    // --- BATCH ANIMATION ---
    const runBatchAnimation = (winnersArr) => {
        setBatchRevealedCount(0); // All waiting/spinning
        
        // Sequential Reveal: Start revealing from left (index 0) to right
        // Delay between reveals = spinStep
        const count = winnersArr.length;
        const BASE_DELAY = 1000; // Time before first reveal

        for (let i = 0; i < count; i++) {
             setTimeout(() => {
                 setBatchRevealedCount(prev => prev + 1);
                 // If last one
                 if (i === count - 1) {
                     setTimeout(() => { finishSpin(winnersArr); }, 1000);
                 }
             }, BASE_DELAY + (i * spinStep * 1000));
        }
    };

    // --- SINGLE SLOT ANIMATION ---
    const runSlotAnimation = (winnersArr) => {
        const firstWinner = winnersArr[0];
        const code = firstWinner.employee_code.toString().trim();
        
        // Padding Logic: Fill rest with Empty Char (' ') not '0'
        let chars = code.split('');
        const SLOT_COUNT = 9;
        while (chars.length < SLOT_COUNT) chars.push(' '); 

        const singleSetHeight = CHAR_MAP.length * STRIP_HEIGHT_SINGLE;
        
        // Randomize offsets slightly to look natural
        setSlotOffsets(prev => prev.map(() => Math.floor(Math.random() * singleSetHeight)));
        
        const BASE_DURATION = 2.0;

        setTimeout(() => {
             // Calculate final offsets
             const finalOffsets = chars.map((char, colIndex) => {
                 let charIdx = CHAR_MAP.indexOf(char);
                 if (charIdx === -1) charIdx = CHAR_MAP.length - 1; // Default to space (last item)

                 // Duration increases by spinStep for each column => Sequential Stop
                 const myDuration = BASE_DURATION + (colIndex * spinStep);
                 
                 // Distance Calculation
                 const rounds = 5 + Math.floor(myDuration * 1.5);
                 return (rounds * CHAR_MAP.length + charIdx) * STRIP_HEIGHT_SINGLE;
             });

             setSlotOffsets(finalOffsets);
             
             const totalDuration = BASE_DURATION + (SLOT_COUNT * spinStep);
             setTimeout(() => { finishSpin(winnersArr); }, totalDuration * 1000 + 500);
        }, 100);
    };

    const finishSpin = (winnersArr) => {
        setIsSpinning(false);
        setPopupWinners(winnersArr);
        fireConfetti();
        fetchWinners();
    };

    const fireConfetti = () => { confetti({ startVelocity: 40, spread: 360, ticks: 100, zIndex: 2500, particleCount: 300, origin: { y: 0.6 } }); };

    const saveSettings = async () => { try { await axios.post('/api/settings', { overlayOpacity, spinStep }); } catch (e) { } };
    const handleBgUpload = async (e) => {
         const file = e.target.files[0]; if (!file) return;
         const formData = new FormData(); formData.append('bgFile', file);
         setUploadingBg(true);
         try { const res = await axios.post('/api/settings', formData); setBgImage(res.data.settings.bgImage); } catch(e){} finally { setUploadingBg(false); }
    };
    
    // MISSING FUNCTIONS ADDED HERE
    const handleResetScore = async () => { if (window.confirm("⚠️ CẢNH BÁO: XÓA TOÀN BỘ KẾT QUẢ?")) try { await axios.post('/api/reset'); } catch(e){} };
    const handleResetAll = async () => { if (window.confirm("⛔ NGUY HIỂM: XÓA SẠCH DỮ LIỆU?")) try { await axios.post('/api/reset-employees'); } catch(e){} };

    // --- RENDER HELPERS ---
    
    // RENDER SLOT (SINGLE)
    const renderSlotMachine = () => {
        return (
            <div className="slot-machine-frame">
                {Array.from({length: 9}).map((_, colIndex) => (
                    <div key={colIndex} className="slot-column">
                        <div className={`slot-strip ${isSpinning ? 'is-spinning' : ''}`}
                            style={{ 
                                transform: `translateY(-${slotOffsets[colIndex]}px)`, 
                                transition: isSpinning ? `transform ${2 + (colIndex * spinStep)}s cubic-bezier(0.15, 0.8, 0.15, 1)` : 'none' 
                            }}>
                            {staticStrip.current.map((char, idx) => (
                                <div key={idx} className="slot-number">
                                    {char === ' ' ? '' : char}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // RENDER GRID (BATCH)
    const renderBatchGrid = () => {
        let displayCount = session ? session.batchSize : currentBatchWinners.length;
        if (currentBatchWinners.length > 0) displayCount = currentBatchWinners.length;
        if (displayCount === 0) displayCount = 0;
        
        // If not spinning and no winners yet, show placeholders for batchSize
        if (!isSpinning && currentBatchWinners.length === 0 && session) displayCount = Math.min(session.batchSize, session.remaining);
        
        const items = Array.from({length: displayCount});
        
        return (
            <div className="batch-grid-container">
                {items.map((_, idx) => {
                    // Determine state for this specific card
                    let status = 'waiting'; 
                    let result = null;

                    if (isSpinning) {
                        // If we have winners data
                        if (currentBatchWinners.length > 0) {
                            if (idx < batchRevealedCount) {
                                status = 'revealed';
                                result = currentBatchWinners[idx];
                            } else {
                                status = 'spinning';
                            }
                        } else {
                             // Should ideally not happen if winners are prescalced, but fallback
                             status = 'spinning';
                        }
                    } else if (currentBatchWinners.length > idx) {
                        status = 'revealed';
                        result = currentBatchWinners[idx];
                    }

                    return ( 
                        <SpinCard key={idx} status={status} result={result} /> 
                    );
                })}
            </div>
        );
    };


    let mainContent;
    const isBatchMode = session && session.batchSize > 1;
    // Decision logic
    if (isBatchMode) mainContent = renderBatchGrid();
    else mainContent = renderSlotMachine();

    let btnText = "QUAY THƯỞNG"; let btnClass = "btn-spin"; let btnDisabled = false;
    if (isSpinning) { btnText = "ĐANG QUAY..."; btnDisabled = true; } 
    else if (!session) { btnText = "VUI LÒNG TẠO GIẢI"; btnDisabled = true; } 
    else if (session.remaining === 0) { btnText = "HOÀN THÀNH (TIẾP TỤC)"; btnClass = "btn-complete"; btnDisabled = false; }


    return (
        <div className="lucky-container" style={{ backgroundImage: `url('${bgImage}')` }}>
             <div className="overlay-bg" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}></div>
             
             {/* HEADER & SETTINGS */}
             <div className="content-wrapper">
                 <div style={{ padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' }}>
                     <div>
                         <h1 style={{margin:0}}>LUCKY DRAW</h1>
                         <button onClick={handleResetScore} style={{marginRight:10, padding:'5px 10px', background:'#e67e22', border:'none', color:'white', borderRadius:4, cursor:'pointer'}}>Reset KQ</button>
                         <button onClick={() => setShowSettings(!showSettings)} style={{background:'none', border:'none', fontSize:24, cursor:'pointer'}}>🎨</button>
                     </div>
                     
                     {showSettings && (
                        <div className="settings-panel">
                            <h3 style={{marginTop:0, borderBottom:'1px solid #777', paddingBottom:10}}>Cấu hình</h3>
                            <button style={{position:'absolute', top:5, right:5, background:'none', border:'none', color:'#ccc', cursor:'pointer'}} onClick={()=>setShowSettings(false)}>✕</button>
                            
                            <label style={{display:'block', marginBottom:5}}>Độ tối nền: {Math.round(overlayOpacity*100)}%</label>
                            <input type="range" min="0" max="0.95" step="0.05" value={overlayOpacity} onChange={e=>setOverlayOpacity(parseFloat(e.target.value))} onMouseUp={saveSettings} onTouchEnd={saveSettings} style={{width:'100%', marginBottom:15}} />
                            
                            <label style={{display:'block', marginBottom:5}}>Khoảng cách quay (giây): {spinStep}s</label>
                            <input type="range" min="0.1" max="1.5" step="0.1" value={spinStep} onChange={e=>setSpinStep(parseFloat(e.target.value))} onMouseUp={saveSettings} onTouchEnd={saveSettings} style={{width:'100%', marginBottom:15}} />
                            
                            <label style={{display:'block', marginBottom:5}}>Ảnh nền:</label>
                             <label className="btn-upload" style={{display:'block', background:'#444', padding:8, textAlign:'center', cursor:'pointer', borderRadius:4}}>
                                {uploadingBg ? 'Đang tải...' : 'Chọn ảnh...'} 
                                <input type="file" accept="image/*" style={{display:'none'}} onChange={handleBgUpload} disabled={uploadingBg}/>
                            </label>
                            
                            <button onClick={handleResetAll} style={{width:'100%', marginTop:20, padding:10, background:'#c0392b', color:'#fff', border:'none', borderRadius:4}}>XÓA TOÀN BỘ DỮ LIỆU</button>
                        </div>
                     )}

                     {/* SESSION CONTROLS */}
                     <div style={{background:'rgba(0,0,0,0.5)', padding:10, borderRadius:8, display:'flex', gap:10}}>
                         {!session ? (
                             <>
                                <input value={inputName} onChange={e=>setInputName(e.target.value)} placeholder="Tên giải" style={{padding:8, borderRadius:4, border:'none'}}/>
                                <input value={inputQty} type="number" onChange={e=>setInputQty(e.target.value)} placeholder="SL" style={{width:60, padding:8, borderRadius:4, border:'none'}}/>
                                {parseInt(inputQty) > 1 && <input value={inputBatchSize} type="number" onChange={e=>setInputBatchSize(e.target.value)} placeholder="Batch" style={{width:60, padding:8, borderRadius:4, border:'none'}}/>}
                                <button onClick={startSession} style={{padding:'8px 15px', background:'#27ae60', color:'#fff', border:'none', borderRadius:4, fontWeight:'bold', cursor:'pointer'}}>TẠO GIẢI</button>
                             </>
                         ) : (
                             <div style={{display:'flex', alignItems:'center', gap:15}}>
                                 <span style={{fontSize:20, color:'#f1c40f', fontWeight:'bold'}}>{session.name}</span>
                                 <span style={{background:'#fff', color:'#000', padding:'2px 8px', borderRadius:4, fontWeight:'bold'}}>Còn: {session.remaining}</span>
                                 {session.remaining > 0 && <button onClick={cancelSession} style={{background:'#e74c3c', color:'#fff', border:'none', padding:'4px 8px', borderRadius:4, cursor:'pointer'}}>Hủy</button>}
                             </div>
                         )}
                     </div>

                     <div style={{textAlign:'right'}}>
                         <div>Người tham gia</div>
                         <div style={{fontSize:30, fontWeight:'bold', color:'#f1c40f'}}>{stats}</div>
                     </div>
                 </div>

                 {/* MAIN CONTENT */}
                 <div style={{flex:1, display:'flex', padding:20, gap:30, overflow:'hidden'}}>
                     <div style={{flex:3, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
                         {mainContent}
                         
                         <button onClick={handleMainButton} disabled={btnDisabled} className={`btn-action ${btnClass}`} style={{opacity: btnDisabled ? 0.6 : 1}}> 
                            {btnText} 
                         </button>
                     </div>
                     
                     <div style={{flex:1, background:'rgba(255,255,255,0.9)', borderRadius:10, padding:20, maxWidth:400, overflowY:'auto'}}>
                         <h3 style={{marginTop:0, color:'#c0392b', borderBottom:'2px solid #c0392b', paddingBottom:5, textAlign:'center'}}>KẾT QUẢ</h3>
                         <div style={{display:'flex', flexDirection:'column', gap:10}}>
                             {winners.map((w,i) => (
                                 <div key={i} style={{display:'flex', justifyContent:'space-between', borderBottom:'1px solid #ddd', paddingBottom:5}}>
                                     <div>
                                         <div style={{fontWeight:'bold'}}>{w.prize_name}</div>
                                         <div style={{fontSize:12, color:'#555'}}>{w.full_name}</div>
                                     </div>
                                     <div style={{fontFamily:'Impact', fontSize:20, color:'#c0392b'}}>
                                         {w.employee_code}
                                     </div>
                                 </div>
                             ))}
                         </div>
                     </div>
                 </div>
             </div>
             
             {/* POPUP RESULT */}
            {popupWinners.length > 0 && (
                <div className="modal-overlay" onClick={() => { if(!isSpinning) setPopupWinners([]); }}>
                    <div className="winner-popup" onClick={(e) => e.stopPropagation()} style={{ width: '90%', maxWidth: '1200px' }}>
                        <h1 style={{ color: '#d4af37', margin: 0, fontSize: 40, marginBottom: 20 }}>🎉 CHÚC MỪNG 🎉</h1>
                        <div style={{ fontSize: 30, color: '#e67e22', marginBottom: 30, fontWeight:'bold' }}>{popupWinners[0].prize_name}</div>
                        
                        <div style={{ 
                            display: 'grid', justifyContent: 'center',
                            gridTemplateColumns: popupWinners.length > 1 ? 'repeat(4, 1fr)' : '1fr', 
                            gap: 20, maxHeight: '60vh', overflowY: 'auto' 
                        }}>
                             {/* POPUP ALWAYS USES SPINCARD */}
                            {popupWinners.map((winner, idx) => ( 
                                <div key={idx} style={popupWinners.length === 1 ? {transform: 'scale(1.5)', margin: 50} : {}}>
                                    <SpinCard status='revealed' result={winner} />
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setPopupWinners([])} style={{marginTop: 30, padding: '12px 40px', fontSize: 18, background: '#333', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer'}}>Đóng</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LuckyBoard;
